import { useEffect, useRef, type ReactNode } from 'react';

interface BookmarkletLinkProps {
  href: string;
  className?: string;
  children: ReactNode;
}

/**
 * React 19 replaces any `javascript:` URL passed through the JSX `href` prop
 * with a stub that just throws ("React has blocked a javascript: URL as a
 * security precaution" — https://react.dev/errors/323), which is the right
 * default when an href could come from untrusted/dynamic input. This one is
 * a fixed, build-time-generated bookmarklet (see scripts/build-bookmarklet.mjs),
 * not user input, and the entire point is for the user to drag it to their
 * bookmarks bar or copy its real link address — so it needs to bypass that
 * sanitization. Setting `href` imperatively via the DOM API after mount,
 * rather than as a JSX prop, does that: React never manages this attribute,
 * so its render-time URL check never runs on it.
 */
export default function BookmarkletLink({ href, className, children }: BookmarkletLinkProps) {
  const anchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    anchorRef.current?.setAttribute('href', href);
  }, [href]);

  return (
    <a ref={anchorRef} className={className} onClick={(e) => e.preventDefault()}>
      {children}
    </a>
  );
}
